/* <gr-cart-drawer> — the primary cart surface.
   Extends the drawer shell and owns every cart mutation in the theme. Add,
   change and update all route through here so there is one place that knows how
   to talk to Shopify, one place that patches the DOM, and one place that
   decides what the shopper is told when something fails. */

class GrCartDrawer extends (window.GrDrawer || HTMLElement) {
  constructor() {
    super();
    this.pending = null;         // AbortController for the in-flight mutation
    this.sequence = 0;           // guards against out-of-order responses
  }

  connectedCallback() {
    super.connectedCallback();
    if (this._cartBound) return;
    this._cartBound = true;

    this.onSubmit = this.onSubmit.bind(this);
    this.onCartClick = this.onCartClick.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);

    // Product forms live in sections that re-render independently, so the
    // listener is delegated from the document rather than bound per form.
    document.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onCartClick);
    this.addEventListener('gr:quantity:change', this.onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onCartClick);
    this.removeEventListener('gr:quantity:change', this.onQuantityChange);
    if (this.pending) this.pending.abort();
    this._cartBound = false;
  }

  get sectionId() { return this.getAttribute('data-section-id'); }

  /* Section IDs are read from the DOM, never assumed. A header inside a section
     group does not have a literal id of "header". */
  sectionsToRender() {
    const ids = [];
    if (this.sectionId) ids.push(this.sectionId);
    document.querySelectorAll('[data-cart-count-section]').forEach((el) => {
      const id = el.getAttribute('data-cart-count-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /* ---------------- Add to cart ---------------- */

  async onSubmit(event) {
    const form = event.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    if (GR.cartType !== 'drawer') return;   // page mode uses native form posting

    event.preventDefault();

    const button = form.querySelector('[data-add-to-cart]');
    if (button && button.getAttribute('aria-disabled') === 'true') return;

    this.setButtonState(button, 'loading');
    this.clearError(form);

    const formData = new FormData(form);
    formData.append('sections', this.sectionsToRender().join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(GR.routes.cart_add, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        // 422 from Shopify means inventory or a selling-plan mismatch — the
        // shopper needs the real reason, not a generic failure.
        this.showError(form, data.description || data.message || GR.strings.cartError);
        this.setButtonState(button, 'idle');
        return;
      }

      // The mutation succeeded. Section rendering is a separate concern: if it
      // fails we still refetch rather than telling the shopper the add failed.
      if (data.sections) this.renderSections(data.sections);
      else await this.refresh();

      this.setButtonState(button, 'idle');
      this.open(button);
      GR.announce(this.updatedMessage());
    } catch (error) {
      this.showError(form, GR.strings.cartError);
      this.setButtonState(button, 'idle');
    }
  }

  /* ---------------- Quantity + remove ---------------- */

  onCartClick(event) {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    this.change(remove.getAttribute('data-cart-remove'), 0);
  }

  onQuantityChange(event) {
    const { key, quantity } = event.detail;
    if (!key) return;
    this.change(key, quantity);
  }

  /* Line items are addressed by key, not variant id: two lines can share a
     variant and differ only by selling plan or line properties. */
  async change(key, quantity) {
    const ticket = ++this.sequence;
    if (this.pending) this.pending.abort();
    this.pending = new AbortController();

    this.setBusy(true);

    try {
      const data = await GR.fetchJSON(GR.routes.cart_change, {
        method: 'POST',
        signal: this.pending.signal,
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this.sectionsToRender(),
          sections_url: window.location.pathname
        })
      });

      // A slower earlier request must never overwrite a newer result.
      if (ticket !== this.sequence) return;

      if (data.sections) this.renderSections(data.sections);
      else await this.refresh();

      GR.announce(this.updatedMessage());
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (ticket !== this.sequence) return;
      this.showError(this, (error.data && error.data.description) || GR.strings.quantityError);
      // Re-sync from the server so the visible quantity always matches the cart.
      await this.refresh();
    } finally {
      if (ticket === this.sequence) {
        this.pending = null;
        this.setBusy(false);
      }
    }
  }

  /* ---------------- Rendering ---------------- */

  /* Patch the dynamic region only. Replacing the whole custom element would
     tear down the open state, the focus trap and the scroll position on every
     quantity tap. */
  renderSections(sections) {
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return;   // a null section is a render failure, not a cart failure

      const parsed = new DOMParser().parseFromString(html, 'text/html');

      if (id === this.sectionId) {
        const incoming = parsed.querySelector('[data-cart-drawer-content]');
        const current = this.querySelector('[data-cart-drawer-content]');
        if (incoming && current) {
          const scroll = current.querySelector('[data-cart-lines]');
          const offset = scroll ? scroll.scrollTop : 0;
          current.innerHTML = incoming.innerHTML;
          const restored = current.querySelector('[data-cart-lines]');
          if (restored) restored.scrollTop = offset;
        }
        const state = parsed.querySelector('[data-cart-state]');
        if (state) this.setAttribute('data-cart-empty', state.getAttribute('data-cart-empty'));
      } else {
        const incoming = parsed.querySelector('[data-cart-count-section="' + id + '"]');
        const current = document.querySelector('[data-cart-count-section="' + id + '"]');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
      }
    });

    document.dispatchEvent(new CustomEvent('gr:cart:updated', { bubbles: true }));
  }

  /* Fallback path when bundled section rendering was unavailable. */
  async refresh() {
    try {
      const url = GR.url('/?sections=' + this.sectionsToRender().join(','));
      const sections = await GR.fetchJSON(url);
      this.renderSections(sections);
    } catch (error) {
      /* Leave the current DOM in place; a stale view beats a blank one. */
    }
  }

  updatedMessage() {
    const total = this.querySelector('[data-cart-subtotal]');
    const base = GR.strings.cartUpdated || 'Cart updated';
    return total ? base + ', ' + total.textContent.trim() : base;
  }

  /* ---------------- State helpers ---------------- */

  setBusy(state) {
    const region = this.querySelector('[data-cart-drawer-content]');
    if (region) region.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  setButtonState(button, state) {
    if (!button) return;
    const label = button.querySelector('[data-add-to-cart-label]');
    if (state === 'loading') {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-loading', '');
      if (label) {
        button.setAttribute('data-idle-label', label.textContent);
        label.textContent = GR.strings.adding || label.textContent;
      }
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('data-loading');
      const idle = button.getAttribute('data-idle-label');
      if (label && idle) label.textContent = idle;
    }
  }

  showError(scope, message) {
    const target = scope.querySelector('[data-cart-error]');
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
    GR.announce(message);
  }

  clearError(scope) {
    const target = scope.querySelector('[data-cart-error]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }
}

if (!customElements.get('gr-cart-drawer')) customElements.define('gr-cart-drawer', GrCartDrawer);
